import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import supabaseService from '../services/supabaseService';
import { useAuth } from '../hooks/useAuth'; // 🔧 استيراد useAuth للوصول إلى دالة signOut الخاصة بالتطبيق
import { FiLock, FiEye, FiEyeOff, FiArrowRight, FiLoader } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';

const ResetPasswordScreen = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(true);
  const [hasValidToken, setHasValidToken] = useState(false);
  const navigate = useNavigate();
  const { signOut } = useAuth(); // 🔧 استخدام دالة signOut من الـ Context
  
  const checkInterval = useRef(null);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;

    const verifyFlow = async () => {
      const { data: { session } } = await supabaseService.client.auth.getSession();
      if (session) {
        setHasValidToken(true);
        setIsResetting(false);
        return;
      }

      checkInterval.current = setInterval(async () => {
        attempts++;
        const { data: { session: currentSession } } = await supabaseService.client.auth.getSession();
        
        if (currentSession) {
          clearInterval(checkInterval.current);
          setHasValidToken(true);
          setIsResetting(false);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval.current);
          setIsResetting(false);
          setHasValidToken(false);
        }
      }, 1000);
    };

    verifyFlow();
    return () => { if (checkInterval.current) clearInterval(checkInterval.current); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error('كلمة المرور قصيرة جداً');
    if (password !== confirmPassword) return toast.error('كلمات المرور غير متطابقة');

    setLoading(true);
    try {
      const { error } = await supabaseService.client.auth.updateUser({ password });
      if (error) throw error;

      toast.success('تم تحديث كلمة المرور');
      
      // 🔧 التعديل هنا: استخدام signOut من useAuth بدلاً من supabaseService.client.auth.signOut
      // هذا سيقوم بتنظيف الحالة isRecoveryMode والسماح بتسجيل الدخول مجدداً
      await signOut();
      
      setTimeout(() => navigate('/auth'), 1500);
    } catch (error) {
      toast.error(error.message || 'حدث خطأ ما');
    } finally {
      setLoading(false);
    }
  };

  // 1. شاشة التحقق (Minimalist Loader)
  if (isResetting) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="mb-4"
        >
          <FiLoader className="text-black w-8 h-8" />
        </motion.div>
        <p className="text-black text-sm tracking-widest uppercase font-light">Verifying Link</p>
      </div>
    );
  }

  // 2. شاشة الخطأ (Error State)
  if (!hasValidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-sm w-full text-center">
          <div className="mb-6 flex justify-center">
            <div className="w-16 h-16 border border-black rounded-full flex items-center justify-center">
              <span className="text-2xl">!</span>
            </div>
          </div>
          <h2 className="text-xl font-bold text-black mb-2 uppercase tracking-tight">رابط غير صالح</h2>
          <p className="text-gray-500 text-sm mb-8 leading-relaxed">
            عذراً، يبدو أن صلاحية هذا الرابط قد انتهت. يرجى طلب رابط جديد للمتابعة.
          </p>
          <button 
            onClick={() => navigate('/forgot-password')}
            className="w-full bg-black text-white py-4 rounded-none hover:bg-gray-900 transition-all text-sm font-bold uppercase tracking-widest"
          >
            طلب رابط جديد
          </button>
        </div>
      </div>
    );
  }

  // 3. الشاشة الرئيسية (Reset Password - Black & White)
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <motion.div 
        initial={{ opacity: 0, y: 10 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <h1 className="text-3xl font-black text-black uppercase tracking-tighter mb-2">تعيين كلمة المرور</h1>
          <p className="text-gray-400 text-xs uppercase tracking-[0.2em]">New Password Security</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mr-1">كلمة المرور الجديدة</label>
            <div className="relative group">
              <input 
                type={showPassword ? "text" : "password"}
                className="w-full p-4 bg-gray-50 border-b-2 border-transparent focus:border-black focus:bg-white transition-all outline-none text-black font-medium"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)} 
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mr-1">تأكيد كلمة المرور</label>
            <div className="relative group">
              <input 
                type={showConfirmPassword ? "text" : "password"}
                className="w-full p-4 bg-gray-50 border-b-2 border-transparent focus:border-black focus:bg-white transition-all outline-none text-black font-medium"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button 
                type="button" 
                onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
              >
                {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          </div>

          <div className="pt-4">
            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-black text-white py-5 flex items-center justify-center gap-3 group hover:bg-gray-800 transition-all disabled:bg-gray-200"
            >
              {loading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <>
                  <span className="text-xs font-bold uppercase tracking-[0.3em]">تحديث البيانات</span>
                  <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </div>
        </form>

        <footer className="mt-12 text-center">
          <button 
            onClick={() => navigate('/auth')}
            className="text-[10px] uppercase tracking-widest text-gray-400 hover:text-black transition-colors"
          >
            العودة لتسجيل الدخول
          </button>
        </footer>
      </motion.div>
    </div>
  );
};

export default ResetPasswordScreen;